import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  DollarSign, TrendingUp, Clock, FileText, Users, CheckCircle2,
  ArrowRight, BarChart3, Shield, Scale, Zap, Star, Loader2,
  ChevronRight, Calculator, Target, Briefcase, ArrowLeft
} from "lucide-react";
import { API } from "@/constants";

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

const TIER_STYLES = {
  low: { gradient: "from-slate-700 to-slate-900", accent: "border-slate-400", badge: "bg-slate-600", ring: "ring-slate-400" },
  medium: { gradient: "from-blue-700 to-indigo-800", accent: "border-blue-500", badge: "bg-blue-600", ring: "ring-blue-500" },
  high: { gradient: "from-amber-600 to-orange-700", accent: "border-amber-500", badge: "bg-amber-600", ring: "ring-amber-500" },
};

const AccountTermsPage = () => {
  const [tiers, setTiers] = useState(null);
  const [selectedTier, setSelectedTier] = useState("low");
  const [roi, setRoi] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calcLoading, setCalcLoading] = useState(false);

  // Custom inputs
  const [customDocs, setCustomDocs] = useState("");
  const [customCases, setCustomCases] = useState("");
  const [customCaseValue, setCustomCaseValue] = useState("");
  const [customRate, setCustomRate] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const [tiersRes, compareRes] = await Promise.all([
          axios.get(`${API}/account/tiers`),
          axios.get(`${API}/account/roi/compare`),
        ]);
        setTiers(tiersRes.data.tiers);
        setComparison(compareRes.data.comparison);
        setRoi(compareRes.data.comparison.low);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const calculateCustomROI = async () => {
    setCalcLoading(true);
    try {
      const params = { tier: selectedTier };
      if (customDocs) params.documents_per_month = parseInt(customDocs);
      if (customCases) params.cases_per_month = parseInt(customCases);
      if (customCaseValue) params.avg_case_value = parseFloat(customCaseValue);
      if (customRate) params.attorney_hourly_rate = parseFloat(customRate);

      const res = await axios.post(`${API}/account/roi`, params);
      setRoi(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setCalcLoading(false);
    }
  };

  const selectTier = (tier) => {
    setSelectedTier(tier);
    if (comparison) setRoi(comparison[tier]);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="account-terms-page">
      {/* Hero */}
      <div className="bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 text-white px-6 py-16">
        <div className="max-w-6xl mx-auto text-center">
          <Badge className="bg-blue-500/20 text-blue-200 border-blue-400/30 mb-4">Account Terms & ROI</Badge>
          <h1 className="text-4xl font-bold mb-3">Invest in Intelligence, Not Hours</h1>
          <p className="text-lg text-blue-200 max-w-2xl mx-auto">
            Every dollar invested in Trialeze returns measurable value through time savings, 
            better case outcomes, and referral revenue.
          </p>
          <div className="flex justify-center gap-8 mt-10">
            {[
              { label: "Avg Monthly ROI", value: "4-12x", icon: TrendingUp },
              { label: "Hours Saved / Month", value: "40-120+", icon: Clock },
              { label: "Referral Revenue", value: "$500-$9,000", icon: DollarSign },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <s.icon className="h-6 w-6 text-blue-300 mx-auto mb-2" />
                <p className="text-2xl font-bold">{s.value}</p>
                <p className="text-xs text-blue-300">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12 space-y-16">
        {/* Pricing Tiers */}
        <section>
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold">Choose Your Volume</h2>
            <p className="text-slate-500 mt-1">All plans include full AI verification workflow</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {tiers && Object.entries(tiers).map(([key, tier]) => {
              const style = TIER_STYLES[key];
              const isSelected = selectedTier === key;
              const roiData = comparison?.[key];
              return (
                <button
                  key={key}
                  onClick={() => selectTier(key)}
                  className={`text-left transition-all ${isSelected ? `ring-2 ${style.ring} shadow-lg scale-[1.02]` : "hover:shadow-md"}`}
                  data-testid={`tier-${key}`}
                >
                  <Card className={`h-full overflow-hidden ${isSelected ? style.accent : ""} border-2`}>
                    <div className={`bg-gradient-to-r ${style.gradient} text-white p-5`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <Badge className={`${style.badge} text-white text-xs mb-2`}>
                            {key === "low" ? "Low Volume" : key === "medium" ? "Medium Volume" : "High Volume"}
                          </Badge>
                          <h3 className="text-xl font-bold">{tier.name}</h3>
                        </div>
                        {key === "medium" && <Star className="h-6 w-6 text-amber-300" />}
                      </div>
                      <div className="mt-3">
                        <span className="text-3xl font-bold">{fmt(tier.price_monthly)}</span>
                        <span className="text-sm opacity-80">/month</span>
                      </div>
                      <p className="text-xs opacity-70 mt-1">{fmt(tier.price_annual)}/year (save {fmt(tier.price_monthly * 12 - tier.price_annual)})</p>
                    </div>
                    <CardContent className="pt-5 pb-5">
                      <div className="grid grid-cols-3 gap-3 mb-4 text-center">
                        <div className="p-2 bg-slate-50 rounded">
                          <p className="text-lg font-bold text-slate-800">{tier.docs_per_month}</p>
                          <p className="text-[10px] text-slate-500">Docs/mo</p>
                        </div>
                        <div className="p-2 bg-slate-50 rounded">
                          <p className="text-lg font-bold text-slate-800">{tier.cases_per_month}</p>
                          <p className="text-[10px] text-slate-500">Cases/mo</p>
                        </div>
                        <div className="p-2 bg-slate-50 rounded">
                          <p className="text-lg font-bold text-slate-800">{tier.referrals_per_month}</p>
                          <p className="text-[10px] text-slate-500">Referrals</p>
                        </div>
                      </div>
                      <ul className="space-y-1.5">
                        {tier.features.map((f, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs text-slate-600">
                            <CheckCircle2 className="h-3.5 w-3.5 text-green-500 mt-0.5 flex-shrink-0" />
                            {f}
                          </li>
                        ))}
                      </ul>
                      <p className="text-[10px] text-slate-400 mt-3 italic">{tier.best_for}</p>
                      {roiData && (
                        <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                          <p className="text-xs font-medium text-green-800">
                            Est. Monthly ROI: <span className="text-lg font-bold">{roiData.summary.roi_multiplier}</span>
                          </p>
                          <p className="text-[10px] text-green-600">
                            {fmt(roiData.summary.total_monthly_value)} value / {fmt(roiData.monthly_cost)} cost
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </button>
              );
            })}
          </div>
        </section>

        {/* ROI Calculator */}
        <section>
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold flex items-center justify-center gap-2">
              <Calculator className="h-6 w-6 text-blue-600" />
              ROI Calculator
            </h2>
            <p className="text-slate-500 mt-1">Customize inputs to see your firm's specific return</p>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Input Panel */}
            <Card className="lg:col-span-1">
              <CardHeader><CardTitle className="text-base">Your Firm's Numbers</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-xs">Documents / Month</Label>
                  <Input type="number" placeholder={`Default: ${tiers?.[selectedTier]?.docs_per_month || 4}`}
                    value={customDocs} onChange={e => setCustomDocs(e.target.value)} data-testid="roi-docs-input" />
                </div>
                <div>
                  <Label className="text-xs">Cases / Month</Label>
                  <Input type="number" placeholder={`Default: ${tiers?.[selectedTier]?.cases_per_month || 4}`}
                    value={customCases} onChange={e => setCustomCases(e.target.value)} data-testid="roi-cases-input" />
                </div>
                <div>
                  <Label className="text-xs">Avg Case Value ($)</Label>
                  <Input type="number" placeholder="Default: $45,000"
                    value={customCaseValue} onChange={e => setCustomCaseValue(e.target.value)} data-testid="roi-casevalue-input" />
                </div>
                <div>
                  <Label className="text-xs">Attorney Hourly Rate ($)</Label>
                  <Input type="number" placeholder="Default: $350"
                    value={customRate} onChange={e => setCustomRate(e.target.value)} data-testid="roi-rate-input" />
                </div>
                <Button onClick={calculateCustomROI} disabled={calcLoading} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="calculate-roi-btn">
                  {calcLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Calculator className="h-4 w-4 mr-2" />}
                  Calculate ROI
                </Button>
              </CardContent>
            </Card>

            {/* Results */}
            {roi && (
              <div className="lg:col-span-2 space-y-4">
                {/* Summary Card */}
                <Card className="border-green-200 bg-gradient-to-r from-green-50 to-emerald-50">
                  <CardContent className="py-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                      <div>
                        <p className="text-3xl font-bold text-green-700">{roi.summary.roi_multiplier}</p>
                        <p className="text-xs text-green-600">Monthly ROI</p>
                      </div>
                      <div>
                        <p className="text-3xl font-bold text-emerald-700">{fmt(roi.summary.net_monthly_roi)}</p>
                        <p className="text-xs text-emerald-600">Net Monthly Value</p>
                      </div>
                      <div>
                        <p className="text-3xl font-bold text-blue-700">{roi.time_savings.total_hours_saved}h</p>
                        <p className="text-xs text-blue-600">Hours Saved / Month</p>
                      </div>
                      <div>
                        <p className="text-3xl font-bold text-amber-700">{fmt(roi.summary.annual_net_roi)}</p>
                        <p className="text-xs text-amber-600">Annual Net ROI</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Time Savings Breakdown */}
                <Card>
                  <CardHeader><CardTitle className="text-base flex items-center gap-2"><Clock className="h-4 w-4 text-blue-600" /> Time Savings</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {[
                        { label: "Document Review", hours: roi.time_savings.document_review_hours, icon: FileText },
                        { label: "Case Research", hours: roi.time_savings.case_research_hours, icon: Scale },
                        { label: "Brief / Motion Drafting", hours: roi.time_savings.brief_drafting_hours, icon: Briefcase },
                      ].map((item, i) => (
                        <div key={i} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <item.icon className="h-4 w-4 text-slate-500" />
                            <span className="text-sm">{item.label}</span>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="w-32 h-2 bg-slate-200 rounded-full overflow-hidden">
                              <div className="h-full bg-blue-500 rounded-full" style={{ width: `${Math.min(100, (item.hours / roi.time_savings.total_hours_saved) * 100)}%` }} />
                            </div>
                            <span className="text-sm font-bold w-16 text-right">{item.hours}h</span>
                          </div>
                        </div>
                      ))}
                      <Separator />
                      <div className="flex justify-between font-medium">
                        <span>Total Hours Saved</span>
                        <span className="text-blue-600">{roi.time_savings.total_hours_saved}h ({fmt(roi.time_savings.equivalent_billable_value)} billable value)</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Financial Impact */}
                <Card>
                  <CardHeader><CardTitle className="text-base flex items-center gap-2"><DollarSign className="h-4 w-4 text-emerald-600" /> Financial Impact</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {[
                        { label: "Direct Labor Savings", value: roi.financial_impact.direct_labor_savings, color: "text-blue-600" },
                        { label: "Referral Revenue", value: roi.financial_impact.referral_revenue, color: "text-emerald-600", note: `${roi.inputs.referrals_per_month} referral(s) x $500-$1,500` },
                        { label: "Intelligence Case Value Increase", value: roi.financial_impact.intelligence_case_value, color: "text-purple-600", note: "12% avg improvement in case outcomes" },
                        { label: "Settlement Improvement", value: roi.financial_impact.settlement_improvement, color: "text-amber-600", note: "8% better settlements with data-driven approach" },
                        { label: "Deadline Risk Mitigation", value: roi.financial_impact.deadline_risk_mitigation, color: "text-red-600", note: "Reduced malpractice exposure" },
                      ].map((item, i) => (
                        <div key={i} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                          <div>
                            <span className="text-sm">{item.label}</span>
                            {item.note && <p className="text-[10px] text-slate-400">{item.note}</p>}
                          </div>
                          <span className={`text-sm font-bold ${item.color}`}>{fmt(item.value)}</span>
                        </div>
                      ))}
                      <Separator className="my-2" />
                      <div className="flex justify-between items-center py-2">
                        <span className="font-bold">Total Monthly Value</span>
                        <span className="text-xl font-bold text-green-600">{fmt(roi.summary.total_monthly_value)}</span>
                      </div>
                      <div className="flex justify-between items-center py-1">
                        <span className="text-sm text-slate-500">Monthly Subscription</span>
                        <span className="text-sm text-red-500">-{fmt(roi.summary.monthly_subscription)}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 bg-green-50 rounded-lg px-3">
                        <span className="font-bold text-green-800">Net Monthly ROI</span>
                        <span className="text-xl font-bold text-green-700">{fmt(roi.summary.net_monthly_roi)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Break-Even */}
                <Card className="border-blue-200">
                  <CardContent className="py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Target className="h-5 w-5 text-blue-600" />
                      <div>
                        <p className="font-medium text-sm">Break-Even Point</p>
                        <p className="text-xs text-slate-500">You only need {roi.summary.break_even_cases} case(s) to cover the subscription</p>
                      </div>
                    </div>
                    <Badge className="bg-blue-100 text-blue-800">{roi.summary.break_even_cases} case(s)</Badge>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </section>

        {/* Standard Return Per Client */}
        <section>
          <Card className="bg-gradient-to-r from-slate-900 to-blue-900 text-white">
            <CardContent className="py-10">
              <div className="text-center max-w-3xl mx-auto">
                <h2 className="text-2xl font-bold mb-6">Standard Return Per Client</h2>
                <div className="grid md:grid-cols-3 gap-6">
                  {[
                    { tier: "Low Volume", cases: "4 cases", cost: "$899", value: "$3,500-$8,000", roi: "4-9x", referral: "1 referral ($500-$1,500)" },
                    { tier: "Medium Volume", cases: "10 cases", cost: "$1,499", value: "$12,000-$25,000", roi: "8-17x", referral: "3 referrals ($1,500-$4,500)" },
                    { tier: "High Volume", cases: "20 cases", cost: "$2,499", value: "$30,000-$60,000", roi: "12-24x", referral: "6 referrals ($3,000-$9,000)" },
                  ].map((t, i) => (
                    <div key={i} className="p-5 rounded-xl bg-white/10 backdrop-blur">
                      <Badge className="bg-white/20 text-white text-xs mb-3">{t.tier}</Badge>
                      <p className="text-3xl font-bold mb-1">{t.roi}</p>
                      <p className="text-xs opacity-70 mb-3">Monthly Return on {t.cost}</p>
                      <div className="text-left space-y-1.5 text-xs opacity-80">
                        <p><ChevronRight className="h-3 w-3 inline mr-1" />{t.cases} / month</p>
                        <p><ChevronRight className="h-3 w-3 inline mr-1" />Value: {t.value}</p>
                        <p><ChevronRight className="h-3 w-3 inline mr-1" />{t.referral}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* CTA */}
        <section className="text-center py-8">
          <h2 className="text-2xl font-bold mb-2">Ready to See Your ROI?</h2>
          <p className="text-slate-500 mb-6">Start with Low Volume and scale as your practice grows</p>
          <div className="flex justify-center gap-4">
            <Link to="/contact">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700" data-testid="request-demo-btn">
                Request Membership <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
            <Link to="/tools">
              <Button size="lg" variant="outline" data-testid="explore-tools-btn">
                Explore Tools <Zap className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AccountTermsPage;
